import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Upload, Settings as SettingsIcon, Package, RefreshCcw, FileText, Link2, BarChart3, Clock, Wallet, Activity, Plus, Trash2, AlertTriangle, ChevronDown, ChevronUp, Eye, Copy, ExternalLink, List, X as XIcon, ShoppingCart, Phone, MessageSquare, Map, RotateCcw, Users, Receipt } from 'lucide-react';
import KaspiAdminPanel from '@/components/kaspi/KaspiAdminPanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useWebSocketContext } from '@/context/WebSocketContext';

import { getApiBaseUrl } from '@/lib/apiBase';

const API = getApiBaseUrl();

function HealthCard({ label, ok, value, sub, icon, onClick }) {
  const color = ok === null ? 'bg-gray-100 text-gray-500 border-gray-200'
    : ok ? 'bg-green-50 text-green-700 border-green-200'
    : 'bg-red-50 text-red-700 border-red-200';
  const dot = ok === null ? 'bg-gray-400' : ok ? 'bg-green-500' : 'bg-red-500';
  return (
    <div
      className={`rounded-lg border p-4 ${color} ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</span>
        <div className="flex items-center gap-1.5">
          {onClick && <FileText className="w-3 h-3 opacity-50" />}
          <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-base font-bold">{value}</span>
      </div>
      {sub && <p className="text-xs opacity-60 mt-1">{sub}</p>}
    </div>
  );
}

const AdminPage = () => {
  const { token } = useAuth();
  const DEFAULT_INTERKOM_PROGRESS_MAX = 40000;
  const [suppliers, setSuppliers] = useState([]);
  const [settings, setSettings] = useState({ rub_to_kzt_rate: 5.5, markup_rate: 1.3, balance_check_interval: 600, reconciliation_check_interval: 600, supply_status_sync_interval: 300 });
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [interkomProgress, setInterkomProgress] = useState({ status: 'idle', current: 0, total: 0 });
  const [importExpectedTotal, setImportExpectedTotal] = useState(null);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newSupplierCurrency, setNewSupplierCurrency] = useState('KZT');
  const progressPollerRef = useRef(null);
  const [errorLogs, setErrorLogs] = useState([]);
  const [errorLogsLoading, setErrorLogsLoading] = useState(false);
  const [errorLogsLimit, setErrorLogsLimit] = useState(200);
  const [errorLogsPath, setErrorLogsPath] = useState('');
  const [errorLogsMessage, setErrorLogsMessage] = useState('');
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchSupplier, setMatchSupplier] = useState('all');
  const [matchLimit, setMatchLimit] = useState(3000);
  const [matchOnlyEmpty, setMatchOnlyEmpty] = useState(true);
  const [matchSummary, setMatchSummary] = useState(null);
  const [wishListCheckMode, setWishListCheckMode] = useState('per_product');
  const [wishListChecking, setWishListChecking] = useState(false);
  // Segments loaded from API
  const [interkomSegments, setInterkomSegments] = useState([]);
  const [selectedInterkomSegments, setSelectedInterkomSegments] = useState([]);
  const [segmentsEditing, setSegmentsEditing] = useState(false);
  const [segmentsDraft, setSegmentsDraft] = useState([]);
  const [segmentsSaving, setSegmentsSaving] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [interkomBalance, setInterkomBalance] = useState(null);
  const [interkomBalanceLoading, setInterkomBalanceLoading] = useState(false);
  const [interkomCabinetOpening, setInterkomCabinetOpening] = useState(false);
  // Health dashboard
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [errorDetailsOpen, setErrorDetailsOpen] = useState(false);
  const [errorDetailsLoading, setErrorDetailsLoading] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [errorDetailsTab, setErrorDetailsTab] = useState('patterns');
  const [errorDetailsHours, setErrorDetailsHours] = useState(1);
  // Share links
  const [shareLinks, setShareLinks] = useState([]);
  const [shareLinksLoading, setShareLinksLoading] = useState(false);
  const [shareLinksTotal, setShareLinksTotal] = useState(0);
  const [shareLinksPage, setShareLinksPage] = useState(1);
  const [shareLinksSearch, setShareLinksSearch] = useState('');
  const [shareLinksSearchInput, setShareLinksSearchInput] = useState('');
  const [shareLinksArchived, setShareLinksArchived] = useState(false);
  const [shareLinksStatusFilter, setShareLinksStatusFilter] = useState('all');
  const [sharePreviewOpen, setSharePreviewOpen] = useState(false);
  const [sharePreviewLink, setSharePreviewLink] = useState(null);
  const [shareDetailsOpen, setShareDetailsOpen] = useState(false);
  const [shareDetailsData, setShareDetailsData] = useState(null);
  const [shareDetailsLoading, setShareDetailsLoading] = useState(false);
  const SHARE_LINKS_PAGE_SIZE = 20;
  const initialProgressFetchedRef = useRef(false);

  // Subscribe to global WebSocket (single connection shared across all pages)
  const { connected: wsConnected, subscribe } = useWebSocketContext();

  useEffect(() => {
    return subscribe((msg) => {
      if (msg.type === 'import_progress') {
        setInterkomProgress((prev) => ({
          ...prev,
          status: msg.status || prev.status,
          current: msg.current ?? prev.current,
          total: msg.total ?? prev.total,
          segment: msg.segment || prev.segment,
        }));
      }
    });
  }, [subscribe]);

  useEffect(() => {
    fetchSuppliers();
    fetchSettings();
    fetchInterkomProgress();
    fetchInterkomBalance();
    fetchErrorLogs();
    fetchMetrics();
    fetchInterkomSegments();
    fetchHealth();
    return () => {
      stopProgressPoller();
    };
  }, []);

  const fetchInterkomCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/products/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const total = response.data?.total;
      return typeof total === 'number' && total >= 0 ? total : 0;
    } catch (error) {
      console.warn('Не удалось получить общее количество товаров Интерком', error);
      return 0;
    }
  };

  const fetchInterkomSegments = async () => {
    try {
      const res = await axios.get(`${API}/admin/interkom/segments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const segs = res.data?.segments || [];
      setInterkomSegments(segs);
      setSelectedInterkomSegments(segs.map((s) => s.id));
    } catch {
      // silent fallback
    }
  };

  const saveInterkomSegments = async () => {
    if (!segmentsDraft.length) return;
    setSegmentsSaving(true);
    try {
      await axios.put(
        `${API}/admin/interkom/segments`,
        { segments: segmentsDraft },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInterkomSegments(segmentsDraft);
      setSelectedInterkomSegments(segmentsDraft.map((s) => s.id));
      setSegmentsEditing(false);
      toast.success('Сегменты Интерком сохранены');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения сегментов');
    } finally {
      setSegmentsSaving(false);
    }
  };

  const fetchHealth = async () => {
    setHealthLoading(true);
    try {
      const res = await axios.get(`${API}/admin/health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHealth(res.data);
    } catch {
      setHealth(null);
    } finally {
      setHealthLoading(false);
    }
  };

  const fetchErrorDetails = async (hours = errorDetailsHours) => {
    setErrorDetailsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/health/errors`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { hours, limit: 500 },
      });
      setErrorDetails(res.data);
    } catch {
      toast.error('Не удалось загрузить детали ошибок');
    } finally {
      setErrorDetailsLoading(false);
    }
  };

  const openErrorDetails = () => {
    setErrorDetailsOpen(true);
    setErrorDetailsTab('patterns');
    fetchErrorDetails(errorDetailsHours);
  };

  const fetchShareLinks = async (page = shareLinksPage, search = shareLinksSearch, archived = shareLinksArchived, statusFilter = shareLinksStatusFilter) => {
    setShareLinksLoading(true);
    try {
      const params = { page, page_size: SHARE_LINKS_PAGE_SIZE, archived };
      if (search.trim()) params.search = search.trim();
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API}/admin/share-links`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setShareLinks(res.data.links || []);
      setShareLinksTotal(res.data.total || 0);
      setShareLinksPage(page);
    } catch {
      setShareLinks([]);
    } finally {
      setShareLinksLoading(false);
    }
  };

  const fetchShareLinkDetails = async (code) => {
    setShareDetailsLoading(true);
    setShareDetailsData(null);
    try {
      const res = await axios.get(`${API}/admin/share-links/${code}/details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShareDetailsData(res.data);
    } catch {
      toast.error('Не удалось загрузить детали ссылки');
    } finally {
      setShareDetailsLoading(false);
    }
  };

  const buildDirectShareUrl = (link, preview = false) => {
    const p = link.params || {};
    const params = new URLSearchParams();
    if (Array.isArray(link.item_ids) && link.item_ids.length > 0) {
      params.set('mode', 'ids');
    } else {
      if (p.q) params.set('q', p.q);
      if (p.inStock === '1') params.set('inStock', '1');
      if (p.newOnly === '1') params.set('newOnly', '1');
      if (p.backOnStock === '1') params.set('backOnStock', '1');
      if (p.sortBy && p.sortBy !== 'name') params.set('sortBy', p.sortBy);
      if (p.sortOrder && p.sortOrder !== 'asc') params.set('sortOrder', p.sortOrder);
      if (p.suppliers) params.set('suppliers', p.suppliers);
      if (p.brands) params.set('brands', p.brands);
      if (p.category) params.set('category', p.category);
    }
    if (link.discount_percent) params.set('discount', link.discount_percent);
    if (link.delivery_days) params.set('delivery', link.delivery_days);
    const expiresAt = Date.parse(link.expires_at || '');
    if (!Number.isNaN(expiresAt)) params.set('expires', Math.floor(expiresAt / 1000));
    params.set('_ref', link.code);
    if (preview) params.set('preview', '1');
    return `/share?${params.toString()}`;
  };

  const buildRecreateUrl = (link) => {
    const p = link.params || {};
    const params = new URLSearchParams({ _recreate: '1' });
    if (p.q) params.set('q', p.q);
    if (p.inStock === '1') params.set('inStock', '1');
    if (p.newOnly === '1') params.set('newOnly', '1');
    if (p.backOnStock === '1') params.set('backOnStock', '1');
    if (p.sortBy) params.set('sortBy', p.sortBy);
    if (p.sortOrder) params.set('sortOrder', p.sortOrder);
    if (p.suppliers) params.set('suppliers', p.suppliers);
    if (p.brands) params.set('brands', p.brands);
    if (p.category) params.set('category', p.category);
    return `/?${params.toString()}`;
  };

  const copyShareLinkUrl = async (link) => {
    const url = `${window.location.origin}/s/${link.code}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Ссылка скопирована');
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  const archiveShareLink = async (code, archive) => {
    try {
      await axios.patch(`${API}/admin/share-links/${code}/archive`,
        { archived: archive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(archive ? 'Ссылка архивирована' : 'Ссылка восстановлена');
      fetchShareLinks(shareLinksPage, shareLinksSearch, shareLinksArchived, shareLinksStatusFilter);
    } catch {
      toast.error('Не удалось изменить статус ссылки');
    }
  };

  const handleShareLinksSearch = (e) => {
    e.preventDefault();
    setShareLinksSearch(shareLinksSearchInput);
    fetchShareLinks(1, shareLinksSearchInput, shareLinksArchived, shareLinksStatusFilter);
  };

  const switchShareLinksTab = (archived) => {
    setShareLinksArchived(archived);
    setShareLinksSearch('');
    setShareLinksSearchInput('');
    setShareLinksStatusFilter('all');
    fetchShareLinks(1, '', archived, 'all');
  };

  const switchShareLinksStatus = (status) => {
    setShareLinksStatusFilter(status);
    fetchShareLinks(1, shareLinksSearch, shareLinksArchived, status);
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/admin/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки поставщиков');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки настроек');
    }
  };

  const lastInterkomStatusRef = useRef('idle');

  const fetchInterkomProgress = async () => {
    try {
      const response = await axios.get(`${API}/admin/import/interkom/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = response.data || {};
      const payloadTotal = typeof payload.total === 'number' ? payload.total : 0;
      const payloadCurrent = typeof payload.current === 'number' ? payload.current : 0;
      const status = payload.status || 'idle';
      const summary = payload.summary || null;
      const summaryTotal = typeof summary?.total === 'number' ? summary.total : null;
      const summaryNew = typeof summary?.new === 'number' ? summary.new : null;
      const summaryUpdated = typeof summary?.updated === 'number' ? summary.updated : null;

      if (summaryTotal && summaryTotal > 0) {
        setImportExpectedTotal(summaryTotal);
      } else if (payloadTotal > 0) {
        setImportExpectedTotal((prev) => (prev && prev > 0 ? prev : payloadTotal));
      }

      // Если после перезагрузки видим "running/processing" - показываем состояние импорта
      if (['running', 'processing'].includes(status)) {
        setImporting(true);
        startProgressPoller();
      } else {
        stopProgressPoller();
        setImporting(false);
        if (status !== 'running' && status !== 'processing') {
          setImportExpectedTotal(null);
        }
      }

      setInterkomProgress((prev) => ({
        status,
        current: payloadCurrent,
        total: importExpectedTotal ?? (payloadTotal > 0 ? payloadTotal : prev.total),
      }));

      const prevStatus = lastInterkomStatusRef.current;
      lastInterkomStatusRef.current = status;

      if (!initialProgressFetchedRef.current) {
        initialProgressFetchedRef.current = true;
        return;
      }

      if (status === 'completed' && summary && prevStatus !== 'completed') {
        const totalText = summaryTotal ?? 0;
        const newText = summaryNew ?? 0;
        const updatedText = summaryUpdated ?? 0;
        toast.success(`Импорт завершён: всего ${totalText}, новых ${newText}, обновлено ${updatedText}`);
      }
      if (status === 'error' && payload.detail && prevStatus !== 'error') {
        toast.error(`Импорт Интерком завершился с ошибкой: ${payload.detail}`);
      }
    } catch (error) {
      console.warn('Не удалось получить прогресс импорта Интерком', error);
    }
  };

  const startProgressPoller = () => {
    if (progressPollerRef.current) return;
    progressPollerRef.current = setInterval(() => {
      fetchInterkomProgress();
    }, 1500);
  };

  const stopProgressPoller = () => {
    if (progressPollerRef.current) {
      clearInterval(progressPollerRef.current);
      progressPollerRef.current = null;
    }
  };

  const createSupplier = async () => {
    if (!newSupplierName) {
      toast.error('Введите название поставщика');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/suppliers`,
        { name: newSupplierName, currency: newSupplierCurrency },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Поставщик добавлен');
      setNewSupplierName('');
      fetchSuppliers();
    } catch (error) {
      toast.error('Ошибка добавления поставщика');
    }
  };

  const updateSettings = async () => {
    try {
      await axios.put(
        `${API}/admin/settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Настройки обновлены');
    } catch (error) {
      toast.error('Ошибка обновления настроек');
    }
  };

  const formatApiError = (error, fallback = 'Ошибка запроса') => {
    const status = error?.response?.status;
    if (status === 413) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        return detail;
      }
      return 'Файл слишком большой для загрузки. Попробуйте меньший файл или увеличьте лимит на сервере.';
    }
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
      return detail.map((item) => item?.msg || JSON.stringify(item)).join('; ');
    }
    if (detail && typeof detail === 'object') {
      return detail.message || detail.msg || [detail.error, detail.error_type].filter(Boolean).join(' — ') || JSON.stringify(detail);
    }
    if (typeof detail === 'string') {
      return detail;
    }
    return error?.message || fallback;
  };

  const toggleInterkomSegment = (segmentId) => {
    setSelectedInterkomSegments((prev) => {
      const exists = prev.includes(segmentId);
      if (exists) {
        const next = prev.filter((id) => id !== segmentId);
        return next.length === 0 ? prev : next;
      }
      return [...prev, segmentId];
    });
  };

  const fetchMetrics = async () => {
    setMetricsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetrics(response.data);
    } catch (error) {
      toast.error('Не удалось загрузить метрики');
    } finally {
      setMetricsLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsersList(data.users || []);
    } catch {
      toast.error('Не удалось загрузить пользователей');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUserRoleChange = async (userId, newRole) => {
    setUpdatingUserId(userId);
    try {
      await axios.patch(
        `${API}/admin/users/${userId}/role`,
        { role: newRole === 'user' ? null : newRole },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUsersList((prev) =>
        prev.map((u) =>
          u.id === userId
            ? { ...u, role: newRole === 'user' ? null : newRole, is_admin: newRole === 'admin' }
            : u,
        ),
      );
      toast.success('Роль обновлена');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка обновления роли');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const formatUptime = (seconds) => {
    if (!seconds || seconds < 0) return '-';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hrs}ч ${mins}м ${secs}с`;
  };

  const formatBalanceDate = (isoString) => {
    if (!isoString) return '';
    try {
      return new Date(isoString).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return '';
    }
  };

  const fetchInterkomBalance = async (force = false) => {
    setInterkomBalanceLoading(true);
    try {
      const response = await axios.get(`${API}/admin/interkom/balance`, {
        params: force ? { force: true } : {},
        headers: { Authorization: `Bearer ${token}` }
      });
      setInterkomBalance(response.data);
    } catch (error) {
      toast.error(formatApiError(error, 'Не удалось получить баланс Интерком'));
    } finally {
      setInterkomBalanceLoading(false);
    }
  };

  const openInterkomCabinet = async () => {
    const cabinetWin = window.open('about:blank', '_blank');
    if (!cabinetWin) {
      toast.error('Разрешите всплывающие окна для входа в Интерком');
      return;
    }

    cabinetWin.document.write(
      "<!doctype html><html lang='ru'><head><meta charset='utf-8'><title>Интерком</title></head>"
      + "<body style='font-family:Arial,sans-serif;padding:24px;color:#334155;'>"
      + "<p>Открываем кабинет Интерком…</p></body></html>"
    );
    cabinetWin.document.close();

    setInterkomCabinetOpening(true);
    try {
      const response = await axios.post(`${API}/admin/interkom/cabinet-session`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const relativeUrl = response.data?.url;
      if (!relativeUrl) {
        throw new Error('Не получен URL кабинета Интерком');
      }
      const cabinetUrl = new URL(relativeUrl, window.location.origin).toString();
      cabinetWin.location.href = cabinetUrl;
      cabinetWin.focus();
    } catch (error) {
      cabinetWin.close();
      toast.error(formatApiError(error, 'Не удалось открыть кабинет Интерком'));
    } finally {
      setInterkomCabinetOpening(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !selectedSupplier) {
      toast.error('Выберите файл и поставщика');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('supplier_id', selectedSupplier);

    try {
      const response = await axios.post(`${API}/admin/upload-price`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      const stats = response.data || {};
      const processed = stats.processed ?? stats.total ?? 0;
      const newCount = stats.new ?? 0;
      const updatedCount = stats.updated ?? 0;
      const unchangedCount = stats.unchanged ?? 0;
      const hiddenCount = stats.hidden ?? 0;
      const backOnStockCount = stats.back_on_stock ?? 0;
      toast.success(
        `Строк обработано: ${processed}. Новых: ${newCount}. Обновлено: ${updatedCount}. Без изменений: ${unchangedCount}. ` +
        `Скрыто: ${hiddenCount}. Вернулось на склад: ${backOnStockCount}.`
      );
      setSelectedFile(null);
    } catch (error) {
      toast.error(formatApiError(error, 'Ошибка загрузки файла'));
    } finally {
      setUploading(false);
    }
  };

  const handleInterkomImport = async () => {
    if (selectedInterkomSegments.length === 0) {
      toast.error('Выберите хотя бы один сегмент Интерком');
      return;
    }
    // Импорт запускается без файла, поэтому просто вызываем API
    setImporting(true);
    // Один раз получаем целевое количество товаров из базы перед стартом
    const baseTotal = await fetchInterkomCount();
    const targetTotal = baseTotal > 0 ? baseTotal : DEFAULT_INTERKOM_PROGRESS_MAX;
    setImportExpectedTotal(targetTotal);
    setInterkomProgress({ status: 'running', current: 0, total: targetTotal });

    // пока импорт идёт — опрашиваем бэк для реального прогресса
    startProgressPoller();

    try {
      const response = await axios.post(
        `${API}/admin/import/interkom`,
        { segments: selectedInterkomSegments },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.status === 'accepted') {
        toast.success('Импорт Интерком запущен, отслеживаем прогресс...');
      } else {
        const expectedTotal = typeof response.data?.total === 'number' && response.data.total > 0
          ? response.data.total
          : null;
        if (expectedTotal) {
          setImportExpectedTotal(expectedTotal);
        }
        toast.success(
          `Импорт завершён: всего ${response.data.total}, новых ${response.data.new}, обновлено ${response.data.updated}`
        );
        stopProgressPoller();
        setImporting(false);
      }
    } catch (error) {
      const status = error?.response?.status;
      const isGatewayTimeout = status === 504;
      const isAlreadyRunning = status === 409;
      if (isGatewayTimeout || isAlreadyRunning) {
        toast.info(
          isAlreadyRunning
            ? 'Импорт уже выполняется, продолжаем отслеживать прогресс'
            : 'Импорт продолжает выполняться на сервере, ждём обновления прогресса'
        );
        // Не сбрасываем прогресс — продолжаем опрос
        setImporting(true);
        startProgressPoller();
        return;
      }
      toast.error(formatApiError(error, 'Ошибка импорта Интерком'));
      stopProgressPoller();
      setImporting(false);
      setImportExpectedTotal(null);
      setInterkomProgress({ status: 'idle', current: 0, total: 0 });
    }
  };

  const handleWishListCheck = async () => {
    if (wishListCheckMode === 'cart') {
      toast.info('В ближайшее время добавим проверку списка ожидания через корзину');
      return;
    }

    setWishListChecking(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/wish-list/check`,
        { mode: wishListCheckMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.status === 'running') {
        toast.info('Проверка списка ожидания уже запущена');
      } else {
        toast.success('Запустили проверку списка ожидания Интерком');
      }
    } catch (error) {
      toast.error(formatApiError(error, 'Не удалось запустить проверку списка ожидания'));
    } finally {
      setWishListChecking(false);
    }
  };

  const handleAutoMatch = async () => {
    if (!token) {
      toast.error('Нужен вход в систему');
      return;
    }
    setMatchLoading(true);
    setMatchSummary(null);
    const supplierId = matchSupplier === 'all' ? '' : matchSupplier;
    try {
      const payload = {
        supplier_id: supplierId || undefined,
        limit: matchLimit || 3000,
        dry_run: false,
        only_without_base: matchOnlyEmpty,
      };
      const { data } = await axios.post(`${API}/admin/products/auto-match`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatchSummary(data);
      toast.success(`Обработано ${data.scanned}, совпадений: ${data.matched}, обновлено: ${data.updated}`);
    } catch (error) {
      toast.error(formatApiError(error, 'Не удалось запустить сопоставление'));
    } finally {
      setMatchLoading(false);
    }
  };

  const fetchErrorLogs = async () => {
    setErrorLogsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/logs/errors`, {
        params: { limit: errorLogsLimit },
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data || {};
      setErrorLogs(Array.isArray(data.lines) ? data.lines : []);
      setErrorLogsPath(data.path || '');
      setErrorLogsMessage(data.message || '');
    } catch (error) {
      toast.error('Не удалось загрузить логи');
    } finally {
      setErrorLogsLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>Админ панель</h1>

      <Tabs defaultValue="upload" className="w-full" onValueChange={(val) => {
        if (val === 'sharelinks') fetchShareLinks(1, '', false);
        if (val === 'users') fetchUsers();
      }}>
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 lg:grid-cols-9">
          <TabsTrigger value="upload" data-testid="upload-tab">Загрузка прайсов</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="suppliers-tab">Поставщики</TabsTrigger>
          <TabsTrigger value="settings" data-testid="settings-tab">Настройки</TabsTrigger>
          <TabsTrigger value="logs" data-testid="logs-tab">Логи</TabsTrigger>
          <TabsTrigger value="metrics" data-testid="metrics-tab">Метрики</TabsTrigger>
          <TabsTrigger value="health" data-testid="health-tab">Здоровье</TabsTrigger>
          <TabsTrigger value="kaspi" data-testid="kaspi-tab">Kaspi Pay</TabsTrigger>
          <TabsTrigger value="sharelinks" data-testid="sharelinks-tab">Ссылки</TabsTrigger>
          <TabsTrigger value="users" data-testid="users-tab">Пользователи</TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload">
          <Card className="bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Upload className="w-5 h-5 mr-2" />
                Загрузка прайс-листа
              </CardTitle>
              <CardDescription>
                Загрузите Excel файл (.xlsx, .xls) с прайсом от поставщика
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Выберите поставщика</Label>
                <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                  <SelectTrigger data-testid="supplier-select">
                    <SelectValue placeholder="Выберите поставщика" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name} ({supplier.currency})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Выберите файл</Label>
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  data-testid="file-input"
                />
                {selectedFile && (
                  <p className="text-sm text-gray-600">Выбран файл: {selectedFile.name}</p>
                )}
              </div>

              <Button
                onClick={handleFileUpload}
                disabled={uploading}
                className="w-full"
                data-testid="upload-button"
              >
                {uploading ? 'Загрузка...' : 'Загрузить прайс'}
              </Button>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Сегменты Интерком</Label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setSegmentsDraft([...interkomSegments]); setSegmentsEditing(true); }}
                  >
                    Управлять
                  </Button>
                </div>

                {/* Segment selection checkboxes for import */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {interkomSegments.map((segment) => (
                    <label
                      key={segment.id}
                      className="flex items-center space-x-2 rounded-lg border px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedInterkomSegments.includes(segment.id)}
                        onCheckedChange={() => toggleInterkomSegment(segment.id)}
                        id={`segment-${segment.id}`}
                      />
                      <span className="text-sm">{segment.name}</span>
                    </label>
                  ))}
                </div>

                {/* Inline segment editor */}
                {segmentsEditing && (
                  <div className="mt-3 p-4 border rounded-lg bg-gray-50 space-y-3">
                    <p className="text-sm font-semibold text-gray-700">Редактирование сегментов</p>
                    {segmentsDraft.map((seg, idx) => {
                      const isDuplicate = segmentsDraft.some((s, i) => i !== idx && s.id && s.id === seg.id);
                      return (
                        <div key={idx} className="flex gap-2 items-center">
                          <Input
                            className={`font-mono text-xs flex-1 ${isDuplicate ? 'border-red-400' : ''}`}
                            placeholder="UUID сегмента (авто)"
                            value={seg.id}
                            onChange={(e) => setSegmentsDraft((prev) => prev.map((s, i) => i === idx ? { ...s, id: e.target.value } : s))}
                            title={isDuplicate ? 'Дублирующийся UUID — исправьте перед сохранением' : ''}
                          />
                          <Input
                            className="flex-1"
                            placeholder="Название"
                            value={seg.name}
                            onChange={(e) => setSegmentsDraft((prev) => prev.map((s, i) => i === idx ? { ...s, name: e.target.value } : s))}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSegmentsDraft((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                      );
                    })}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const newId = crypto.randomUUID?.() || `seg-${Date.now()}-${Math.random().toString(36).slice(2)}`;
                        setSegmentsDraft((prev) => [...prev, { id: newId, name: '' }]);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Добавить сегмент
                    </Button>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={saveInterkomSegments} disabled={segmentsSaving}>
                        {segmentsSaving ? 'Сохраняем…' : 'Сохранить'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setSegmentsEditing(false)}>Отмена</Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 border rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
                    <Wallet className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Баланс Интерком</div>
                    <div className={`text-2xl font-bold ${Number(interkomBalance?.amount || 0) < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                      {interkomBalance?.display || '—'}
                    </div>
                    {interkomBalance?.fetched_at && (
                      <div className="text-xs text-gray-500">
                        Обновлено: {formatBalanceDate(interkomBalance.fetched_at)}
                        {interkomBalance.cached ? ' · кэш' : ''}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:w-auto w-full">
                  <Button
                    variant="outline"
                    onClick={() => fetchInterkomBalance(true)}
                    disabled={interkomBalanceLoading}
                    className="sm:w-auto w-full"
                  >
                    <RefreshCcw className={`w-4 h-4 mr-2 ${interkomBalanceLoading ? 'animate-spin' : ''}`} />
                    {interkomBalanceLoading ? 'Обновляем...' : 'Обновить'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={openInterkomCabinet}
                    disabled={interkomCabinetOpening}
                    className="sm:w-auto w-full"
                  >
                    <ExternalLink className={`w-4 h-4 mr-2 ${interkomCabinetOpening ? 'animate-pulse' : ''}`} />
                    {interkomCabinetOpening ? 'Открываем...' : 'Кабинет Интерком'}
                  </Button>
                </div>
              </div>

              <Button
                variant="outline"
                onClick={handleInterkomImport}
                disabled={importing}
                className="w-full flex items-center justify-center"
              >
                {importing ? (
                  'Импортируем Интерком...'
                ) : (
                  <span className="flex items-center">
                    <RefreshCcw className="w-4 h-4 mr-2" />
                    Импорт Интерком
                  </span>
                )}
              </Button>
              {(importing || ['running', 'processing'].includes(interkomProgress.status)) && (
                <div className="w-full mt-3">
                  {(() => {
                    const numerator = Math.max(interkomProgress.current, 0);
                    // Denominator always >= numerator so bar never shows e.g. 12000/10000
                    const target = Math.max(
                      numerator,
                      importExpectedTotal ?? 0,
                      interkomProgress.total ?? 0,
                      DEFAULT_INTERKOM_PROGRESS_MAX,
                    );
                    const denom = Math.max(target, 1);
                    const percent = Math.min(Math.round((numerator / denom) * 100), 100);
                    return (
                      <>
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>
                            {importing
                              ? interkomProgress.segment
                                ? `Сегмент: ${interkomProgress.segment}`
                                : 'Импорт Интерком…'
                              : 'Импорт завершён'}
                          </span>
                          <span>
                            {numerator} / {target} ({percent}%)
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" aria-valuemin={0} aria-valuemax={target}>
                          <div
                            className="h-full bg-blue-600 transition-all duration-300"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <RefreshCcw className="w-4 h-4 text-gray-700" />
                  <div>
                    <div className="font-semibold text-gray-900">Проверка списка ожидания</div>
                    <div className="text-sm text-gray-600">Товары Интерком со статусом «ожидаются»</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1 md:col-span-2">
                    <Label>Способ проверки</Label>
                    <Select value={wishListCheckMode} onValueChange={setWishListCheckMode}>
                      <SelectTrigger data-testid="wish-check-mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per_product">Проверка списка ожидания через запрос каждого товара</SelectItem>
                        <SelectItem value="cart">Проверка списка ожидания через корзину</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleWishListCheck}
                      disabled={wishListChecking}
                      className="w-full"
                      data-testid="wish-check-button"
                    >
                      {wishListChecking ? 'Проверяем…' : 'Проверить ожидаемые'}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-gray-500">При выборе варианта через корзину пока показываем заглушку.</p>
              </div>

              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-gray-700" />
                  <div>
                    <div className="font-semibold text-gray-900">Автосопоставление с Интерком</div>
                    <div className="text-sm text-gray-600">Проставляет связи base_product для выбранного поставщика или всех сразу.</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label>Поставщик (опционально)</Label>
                    <Select value={matchSupplier} onValueChange={setMatchSupplier}>
                      <SelectTrigger>
                        <SelectValue placeholder="Все поставщики" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Все</SelectItem>
                        {suppliers
                          .filter((s) => s.name !== 'Интерком')
                          .map((supplier) => (
                            <SelectItem key={supplier.id} value={supplier.id}>
                              {supplier.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Лимит строк</Label>
                    <Input
                      type="number"
                      min="100"
                      max="20000"
                      value={matchLimit}
                      onChange={(e) => setMatchLimit(Number(e.target.value) || 0)}
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Checkbox
                      id="match-empty-only"
                      checked={matchOnlyEmpty}
                      onCheckedChange={(val) => setMatchOnlyEmpty(Boolean(val))}
                    />
                    <Label htmlFor="match-empty-only" className="text-sm text-gray-700">
                      Только без base_product
                    </Label>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Button onClick={handleAutoMatch} disabled={matchLoading}>
                    {matchLoading ? 'Сопоставляем…' : 'Автосопоставить'}
                  </Button>
                  {matchSummary && (
                    <div className="text-sm text-gray-700">
                      Проверено {matchSummary.scanned}, совпадений {matchSummary.matched}, обновлено {matchSummary.updated}
                    </div>
                  )}
                </div>
                {matchSummary?.sample && matchSummary.sample.length > 0 && (
                  <div className="rounded-md border bg-gray-50 p-3 max-h-48 overflow-y-auto">
                    <div className="text-xs font-semibold text-gray-700 mb-1">Примеры совпадений</div>
                    <ul className="text-sm text-gray-800 space-y-1">
                      {matchSummary.sample.slice(0, 10).map((row, idx) => (
                        <li key={idx} className="flex items-start justify-between gap-2">
                          <span className="flex-1">
                            {row.product_name} ({row.product_articul || 'без артикула'}) — {row.supplier_name}
                          </span>
                          <span className="text-xs text-gray-600">
                            → {row.base_product_name} ({row.base_product_code})
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800 font-medium mb-2">Ожидаемые колонки в Excel:</p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Код / код</li>
                  <li>• Артикул / артикул</li>
                  <li>• Название / наименование / name</li>
                  <li>• Цена / цена / price</li>
                  <li>• Остаток / количество / in_stock (опционально)</li>
                  <li>• Бренд / бренд / brand (опционально)</li>
                  <li>• OEM / oem (опционально)</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metrics">
          <Card className="bg-white/90 backdrop-blur-sm">
            <CardHeader className="flex flex-col gap-1">
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Метрики
              </CardTitle>
              <CardDescription>Базовые показатели работы backend</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border bg-white/80">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-600">Всего запросов</CardTitle>
                  </CardHeader>
                  <CardContent className="text-2xl font-bold">{metrics?.requests_total ?? '—'}</CardContent>
                </Card>
                <Card className="border bg-white/80">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-sm text-gray-600">Ошибки</CardTitle>
                    </div>
                    <Clock className="w-4 h-4 text-red-500" />
                  </CardHeader>
                  <CardContent className="text-2xl font-bold text-red-600">{metrics?.requests_errors ?? '—'}</CardContent>
                </Card>
                <Card className="border bg-white/80">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-600">Uptime</CardTitle>
                  </CardHeader>
                  <CardContent className="text-lg font-semibold">{formatUptime(metrics?.uptime_seconds)}</CardContent>
                </Card>
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={fetchMetrics} disabled={metricsLoading} data-testid="metrics-refresh">
                  {metricsLoading ? 'Обновляем…' : 'Обновить'}
                </Button>
                <p className="text-sm text-gray-600">Данные по запросу, без автообновления</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Health Tab */}
        <TabsContent value="health">
          <Card className="bg-white/90 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Здоровье системы
                </CardTitle>
                <CardDescription>Статус всех компонентов в реальном времени</CardDescription>
              </div>
              <div className="flex items-center gap-3">
                {wsConnected && (
                  <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-200">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    WS
                  </span>
                )}
                <Button size="sm" variant="outline" onClick={fetchHealth} disabled={healthLoading}>
                  <RefreshCcw className={`w-4 h-4 mr-1 ${healthLoading ? 'animate-spin' : ''}`} />
                  Обновить
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!health ? (
                <p className="text-gray-500 text-sm">{healthLoading ? 'Загружаем…' : 'Нет данных'}</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <HealthCard label="MongoDB" ok={health.db_ok} value={health.db_ok ? 'Подключена' : 'Ошибка'} />
                  <HealthCard
                    label="Redis"
                    ok={health.redis_configured ? health.redis_ok : null}
                    value={!health.redis_configured ? 'Не настроен' : health.redis_ok ? 'Подключён' : 'Ошибка'}
                  />
                  <HealthCard
                    label="Telegram"
                    ok={health.telegram_configured}
                    value={health.telegram_configured ? `✓ (${health.telegram_transport})` : 'Не настроен'}
                  />
                  <HealthCard label="Uptime" ok={true} value={formatUptime(health.uptime_seconds)} icon={<Clock className="w-4 h-4" />} />
                  <HealthCard
                    label="Запросы / Ошибки"
                    ok={health.requests_errors === 0}
                    value={`${health.requests_total} / ${health.requests_errors}`}
                  />
                  <HealthCard
                    label="Ошибки за час"
                    ok={health.errors_last_hour === 0}
                    value={health.errors_last_hour === 0 ? '0' : `⚠ ${health.errors_last_hour}`}
                    onClick={health.errors_last_hour > 0 ? openErrorDetails : undefined}
                  />
                  <HealthCard
                    label="WS соединений"
                    ok={true}
                    value={String(health.ws_connections ?? 0)}
                  />
                  <HealthCard
                    label="Импорт Интерком"
                    ok={health.interkom_import_status !== 'error'}
                    value={health.interkom_import_status === 'done' ? 'Завершён' : health.interkom_import_status === 'running' ? '▶ Идёт' : health.interkom_import_status === 'error' ? '✗ Ошибка' : 'Ожидание'}
                    sub={health.interkom_import_updated_at ? new Date(health.interkom_import_updated_at).toLocaleString('ru-RU') : null}
                  />
                  <HealthCard
                    label="Баланс Интерком"
                    ok={!!health.interkom_balance}
                    value={health.interkom_balance || '—'}
                    icon={<Wallet className="w-4 h-4" />}
                    sub={health.interkom_balance_cached_at ? `кэш: ${new Date(health.interkom_balance_cached_at).toLocaleTimeString('ru-RU')}` : null}
                  />
                  <HealthCard
                    label="Последняя сверка"
                    ok={!!health.last_reconciliation_at}
                    value={health.last_reconciliation_at ? new Date(health.last_reconciliation_at).toLocaleString('ru-RU') : 'Нет данных'}
                  />
                  <HealthCard
                    label="Wish-list монитор"
                    ok={!!health.wish_list_last_check_at}
                    value={health.wish_list_last_check_at ? new Date(health.wish_list_last_check_at).toLocaleString('ru-RU') : 'Ещё не проверялся'}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Error Details Modal */}
          <Dialog open={errorDetailsOpen} onOpenChange={setErrorDetailsOpen}>
            <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Детали ошибок
                  {errorDetails && <span className="text-sm font-normal text-gray-500">— {errorDetails.total} за {errorDetails.window_hours}ч</span>}
                </DialogTitle>
              </DialogHeader>

              {/* Time window selector */}
              <div className="flex items-center gap-2 text-sm border-b pb-3">
                <span className="text-gray-500">Окно:</span>
                {[1, 3, 6, 24].map(h => (
                  <button
                    key={h}
                    onClick={() => { setErrorDetailsHours(h); fetchErrorDetails(h); }}
                    className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${errorDetailsHours === h ? 'bg-red-100 border-red-300 text-red-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                  >{h}ч</button>
                ))}
                {errorDetailsLoading && <RefreshCcw className="w-3.5 h-3.5 animate-spin text-gray-400 ml-auto" />}
              </div>

              {/* Tabs */}
              <div className="flex gap-1 text-sm border-b">
                {[['patterns', 'Топ паттернов'], ['loggers', 'По компоненту'], ['list', 'Все записи']].map(([id, label]) => (
                  <button
                    key={id}
                    onClick={() => setErrorDetailsTab(id)}
                    className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${errorDetailsTab === id ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                  >{label}</button>
                ))}
              </div>

              <div className="flex-1 overflow-auto min-h-0">
                {errorDetailsLoading && !errorDetails ? (
                  <p className="text-gray-400 text-sm p-4">Загружаем…</p>
                ) : !errorDetails ? (
                  <p className="text-gray-400 text-sm p-4">Нет данных</p>
                ) : errorDetails.total === 0 ? (
                  <p className="text-green-600 text-sm p-4">Ошибок за выбранный период нет</p>
                ) : (
                  <>
                    {errorDetailsTab === 'patterns' && (
                      <div className="divide-y">
                        {errorDetails.top_patterns.map((p, i) => (
                          <div key={i} className="px-4 py-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-bold text-red-600">{p.count}×</span>
                              <span className="text-xs text-gray-400">{p.pct}%</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-1 mb-2">
                              <div className="bg-red-400 h-1 rounded-full" style={{ width: `${p.pct}%` }} />
                            </div>
                            <p className="text-xs text-gray-700 font-mono break-all leading-relaxed">{p.message}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {errorDetailsTab === 'loggers' && (
                      <div className="divide-y">
                        {errorDetails.by_logger.map((l, i) => {
                          const pct = Math.round(l.count * 100 / errorDetails.total);
                          return (
                            <div key={i} className="px-4 py-3 flex items-center gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-mono text-gray-700 truncate">{l.logger}</p>
                                <div className="w-full bg-gray-100 rounded-full h-1 mt-1.5">
                                  <div className="bg-red-400 h-1 rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <span className="text-sm font-bold text-red-600">{l.count}</span>
                                <span className="text-xs text-gray-400 ml-1">{pct}%</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {errorDetailsTab === 'list' && (
                      <div className="divide-y font-mono">
                        {[...errorDetails.errors].reverse().map((e, i) => (
                          <div key={i} className="px-4 py-2.5 hover:bg-gray-50">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-xs text-gray-400 shrink-0">{e.ts}</span>
                              <span className="text-xs text-red-500 font-semibold shrink-0">{e.level}</span>
                              <span className="text-xs text-blue-600 truncate">{e.logger}</span>
                              <span className="text-xs text-gray-400 truncate hidden sm:block">{e.location.split('/').pop()}</span>
                            </div>
                            <p className="text-xs text-gray-700 break-all leading-relaxed">{e.message}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers">
          <Card className="bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Package className="w-5 h-5 mr-2" />
                Управление поставщиками
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add New Supplier */}
              <div className="p-4 border rounded-lg space-y-4">
                <h3 className="font-semibold">Добавить поставщика</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Название</Label>
                    <Input
                      value={newSupplierName}
                      onChange={(e) => setNewSupplierName(e.target.value)}
                      placeholder="Название поставщика"
                      data-testid="new-supplier-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Валюта</Label>
                    <Select value={newSupplierCurrency} onValueChange={setNewSupplierCurrency}>
                      <SelectTrigger data-testid="new-supplier-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="KZT">KZT (Тенге)</SelectItem>
                        <SelectItem value="RUB">RUB (Рубль)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={createSupplier} data-testid="add-supplier-button">
                  Добавить поставщика
                </Button>
              </div>

              {/* Suppliers List */}
              <div className="space-y-2">
                <h3 className="font-semibold">Список поставщиков</h3>
                {suppliers.map((supplier, index) => (
                  <div
                    key={supplier.id}
                    className="flex justify-between items-center p-4 border rounded-lg"
                    data-testid={`supplier-item-${index}`}
                  >
                    <div>
                      <p className="font-medium">{supplier.name}</p>
                      <p className="text-sm text-gray-600">Валюта: {supplier.currency}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card className="bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center">
                <SettingsIcon className="w-5 h-5 mr-2" />
                Настройки системы
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Курс RUB → KZT</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.rub_to_kzt_rate}
                  onChange={(e) => setSettings({ ...settings, rub_to_kzt_rate: parseFloat(e.target.value) })}
                  data-testid="rub-rate-input"
                />
                <p className="text-sm text-gray-600">Текущий курс: 1 RUB = {settings.rub_to_kzt_rate} KZT</p>
              </div>

              <div className="space-y-2">
                <Label>Коэффициент наценки</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.markup_rate}
                  onChange={(e) => setSettings({ ...settings, markup_rate: parseFloat(e.target.value) })}
                  data-testid="markup-rate-input"
                />
                <p className="text-sm text-gray-600">Текущая наценка: {((settings.markup_rate - 1) * 100).toFixed(0)}%</p>
              </div>

              <div className="border-t pt-4 mt-2">
                <p className="text-sm font-semibold text-gray-700 mb-3">Интервалы мониторинга Интерком</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Проверка баланса (мин.)</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={Math.round((settings.balance_check_interval || 600) / 60)}
                      onChange={(e) => setSettings({ ...settings, balance_check_interval: Math.max(60, parseInt(e.target.value || '1') * 60) })}
                    />
                    <p className="text-xs text-gray-500">Сейчас: каждые {Math.round((settings.balance_check_interval || 600) / 60)} мин.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Проверка новых документов (мин.)</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={Math.round((settings.reconciliation_check_interval || 600) / 60)}
                      onChange={(e) => setSettings({ ...settings, reconciliation_check_interval: Math.max(60, parseInt(e.target.value || '1') * 60) })}
                    />
                    <p className="text-xs text-gray-500">Сейчас: каждые {Math.round((settings.reconciliation_check_interval || 600) / 60)} мин.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Статусы позиций поставок (мин.)</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={Math.round((settings.supply_status_sync_interval || 300) / 60)}
                      onChange={(e) => setSettings({ ...settings, supply_status_sync_interval: Math.max(60, parseInt(e.target.value || '1') * 60) })}
                    />
                    <p className="text-xs text-gray-500">Сейчас: каждые {Math.round((settings.supply_status_sync_interval || 300) / 60)} мин.</p>
                  </div>
                </div>
              </div>

              <Button onClick={updateSettings} className="w-full" data-testid="save-settings-button">
                Сохранить настройки
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card className="bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Ошибки бэкенда
              </CardTitle>
              <CardDescription>Последние строки из файла error-логов</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-2">
                  <Label htmlFor="error-limit">Сколько строк (1-2000)</Label>
                  <Input
                    id="error-limit"
                    type="number"
                    min="1"
                    max="2000"
                    value={errorLogsLimit}
                    onChange={(e) => setErrorLogsLimit(Number(e.target.value) || 0)}
                    className="w-32"
                    data-testid="error-logs-limit"
                  />
                </div>
                <Button onClick={fetchErrorLogs} disabled={errorLogsLoading} data-testid="refresh-logs-button">
                  {errorLogsLoading ? 'Загрузка…' : 'Обновить логи'}
                </Button>
                {errorLogsPath && (
                  <p className="text-sm text-gray-600 break-all">Файл: {errorLogsPath}</p>
                )}
              </div>

              {errorLogsMessage && (
                <p className="text-sm text-amber-600">Комментарий: {errorLogsMessage}</p>
              )}

              <div className="border rounded-md bg-black text-green-100 p-3 max-h-96 overflow-auto">
                <pre className="whitespace-pre-wrap text-sm leading-relaxed">
                  {errorLogs.length > 0 ? errorLogs.join('\n') : 'Лог пуст или пока недоступен'}
                </pre>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="kaspi">
          <KaspiAdminPanel token={token} />
        </TabsContent>

        {/* Share Links Tab */}
        <TabsContent value="sharelinks">
          <Card className="bg-white/90 backdrop-blur-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="w-5 h-5" />
                  Отправленные ссылки
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => fetchShareLinks(shareLinksPage, shareLinksSearch, shareLinksArchived, shareLinksStatusFilter)} disabled={shareLinksLoading}>
                  <RefreshCcw className={`w-4 h-4 mr-1.5 ${shareLinksLoading ? 'animate-spin' : ''}`} />
                  Обновить
                </Button>
              </div>

              {/* Активные / Архив */}
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit mb-3">
                {[{ label: 'Активные', value: false }, { label: 'Архив', value: true }].map(({ label, value }) => (
                  <button key={label} onClick={() => switchShareLinksTab(value)}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${shareLinksArchived === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                    {label}
                  </button>
                ))}
              </div>

              {/* Status filter */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                {[
                  { label: 'Все', value: 'all' },
                  { label: 'Не смотрели', value: 'not_viewed' },
                  { label: 'Смотрели', value: 'viewed' },
                  { label: 'Нажали заказать', value: 'ordered' },
                ].map(({ label, value }) => (
                  <button key={value} onClick={() => switchShareLinksStatus(value)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      shareLinksStatusFilter === value
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                    }`}>{label}
                  </button>
                ))}
              </div>

              {/* Search */}
              <form onSubmit={handleShareLinksSearch} className="flex gap-2">
                <Input
                  placeholder="Поиск по имени или телефону..."
                  value={shareLinksSearchInput}
                  onChange={(e) => setShareLinksSearchInput(e.target.value)}
                  className="max-w-xs h-8 text-sm"
                />
                <Button type="submit" variant="outline" size="sm">Найти</Button>
                {shareLinksSearch && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => {
                    setShareLinksSearchInput(''); setShareLinksSearch('');
                    fetchShareLinks(1, '', shareLinksArchived, shareLinksStatusFilter);
                  }}>Сбросить</Button>
                )}
              </form>
              <CardDescription className="mt-2 text-xs">
                {shareLinksTotal} {shareLinksArchived ? 'в архиве' : 'активных'}{shareLinksSearch ? ` · поиск: «${shareLinksSearch}»` : ''}
              </CardDescription>
            </CardHeader>

            <CardContent>
              {shareLinksLoading ? (
                <div className="flex justify-center py-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : shareLinks.length === 0 ? (
                <p className="text-center text-gray-500 py-10">
                  {shareLinksSearch ? 'Ничего не найдено' : shareLinksArchived ? 'Архив пуст' : 'Ссылки ещё не создавались'}
                </p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-gray-500 uppercase tracking-wide">
                          <th className="pb-2 pr-3">Клиент</th>
                          <th className="pb-2 pr-3">Запрос / условия</th>
                          <th className="pb-2 pr-3 text-center">Просм.</th>
                          <th className="pb-2 pr-3 text-center">Заказов</th>
                          <th className="pb-2 pr-3">Статус</th>
                          <th className="pb-2 pr-3 whitespace-nowrap">Создана</th>
                          <th className="pb-2">Действия</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {shareLinks.map((link) => {
                          const q = link.params?.q || '';
                          const filters = [
                            link.params?.inStock === '1' && 'В наличии',
                            link.params?.newOnly === '1' && 'Новинки',
                            link.params?.sortBy === 'client_price' && `Цена ${link.params?.sortOrder === 'desc' ? '↓' : '↑'}`,
                          ].filter(Boolean);
                          const fmt = (iso) => iso ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
                          const views = link.views || 0;
                          const orders = link.order_clicks || 0;
                          const status = link.status || (orders > 0 ? 'ordered' : views > 0 ? 'viewed' : 'not_viewed');

                          const statusBadge = status === 'ordered'
                            ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium"><ShoppingCart className="w-3 h-3" />Заказал</span>
                            : status === 'viewed'
                            ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium"><Eye className="w-3 h-3" />Смотрел</span>
                            : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs font-medium">Не смотрел</span>;

                          return (
                            <tr key={link.id} className={`hover:bg-gray-50 transition-colors ${link.archived ? 'opacity-60' : ''} ${status === 'ordered' ? 'bg-green-50/30' : ''}`}>
                              <td className="py-2.5 pr-3">
                                <div className={`font-medium text-sm ${link.recipient_name ? 'text-gray-900' : 'text-gray-400 italic'}`}>
                                  {link.recipient_name || 'без имени'}
                                </div>
                                {link.recipient_phone && <div className="text-xs text-gray-500">{link.recipient_phone}</div>}
                                <div className="text-xs text-gray-300 font-mono">{link.code}</div>
                              </td>
                              <td className="py-2.5 pr-3 max-w-[180px]">
                                <div className="text-gray-800 text-sm truncate font-medium">{q || <span className="text-gray-400 italic text-xs">—</span>}</div>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {link.discount_percent && (
                                    <span className="px-1.5 py-0 rounded-full bg-red-50 text-red-600 text-xs font-bold">-{link.discount_percent}%</span>
                                  )}
                                  {filters.map(f => <span key={f} className="px-1.5 rounded-full bg-blue-50 text-blue-600 text-xs">{f}</span>)}
                                </div>
                              </td>
                              <td className="py-2.5 pr-3 text-center">
                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${views === 0 ? 'bg-gray-100 text-gray-400' : views >= 3 ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                  {views}
                                </span>
                              </td>
                              <td className="py-2.5 pr-3 text-center">
                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${orders === 0 ? 'bg-gray-100 text-gray-400' : 'bg-green-100 text-green-700'}`}>
                                  {orders}
                                </span>
                              </td>
                              <td className="py-2.5 pr-3">{statusBadge}</td>
                              <td className="py-2.5 pr-3 text-gray-400 whitespace-nowrap text-xs">{fmt(link.created_at)}</td>
                              <td className="py-2.5">
                                <div className="flex items-center gap-1">
                                  <button onClick={() => window.open(buildDirectShareUrl(link), '_blank')} title="Открыть как клиент"
                                    className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors">
                                    <ExternalLink className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={() => copyShareLinkUrl(link)} title="Скопировать ссылку"
                                    className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors">
                                    <Copy className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={() => window.open(buildRecreateUrl(link), '_blank')} title="Пересоздать подборку"
                                    className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors">
                                    <RotateCcw className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={() => { setSharePreviewLink(link); setSharePreviewOpen(true); }} title="Предпросмотр"
                                    className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors">
                                    <Eye className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={() => { setShareDetailsOpen(true); fetchShareLinkDetails(link.code); }} title="История событий"
                                    className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors">
                                    <List className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={() => archiveShareLink(link.code, !link.archived)}
                                    title={link.archived ? 'Восстановить' : 'В архив'}
                                    className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors text-xs ${link.archived ? 'text-blue-500 hover:text-blue-700' : 'text-gray-300 hover:text-red-500'}`}>
                                    {link.archived ? '↩' : '×'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {shareLinksTotal > SHARE_LINKS_PAGE_SIZE && (
                    <div className="flex items-center justify-between mt-4 pt-3 border-t">
                      <span className="text-xs text-gray-500">
                        Страница {shareLinksPage} из {Math.ceil(shareLinksTotal / SHARE_LINKS_PAGE_SIZE)} · Всего {shareLinksTotal}
                      </span>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" disabled={shareLinksPage <= 1 || shareLinksLoading}
                          onClick={() => fetchShareLinks(shareLinksPage - 1, shareLinksSearch, shareLinksArchived, shareLinksStatusFilter)}>← Назад</Button>
                        <Button size="sm" variant="outline" disabled={shareLinksPage >= Math.ceil(shareLinksTotal / SHARE_LINKS_PAGE_SIZE) || shareLinksLoading}
                          onClick={() => fetchShareLinks(shareLinksPage + 1, shareLinksSearch, shareLinksArchived, shareLinksStatusFilter)}>Вперёд →</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* ── Preview Modal ─────────────────────────────────── */}
          {sharePreviewOpen && sharePreviewLink && (
            <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setSharePreviewOpen(false)}>
              <div className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden max-h-[90vh]" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">Предпросмотр</div>
                    <div className="text-xs text-gray-500">{sharePreviewLink.recipient_name || sharePreviewLink.code}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => window.open(buildDirectShareUrl(sharePreviewLink), '_blank')}
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> Открыть
                    </button>
                    <button onClick={() => setSharePreviewOpen(false)} className="p-1 rounded hover:bg-gray-100 text-gray-400">
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <iframe
                  src={buildDirectShareUrl(sharePreviewLink, true)}
                  title="Предпросмотр клиентской страницы"
                  className="flex-1 border-0"
                  style={{ height: 680, minHeight: 680 }}
                />
              </div>
            </div>
          )}

          {/* ── Details Modal ─────────────────────────────────── */}
          {shareDetailsOpen && (
            <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShareDetailsOpen(false)}>
              <div className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden max-h-[90vh] w-full max-w-lg" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b">
                  <div className="font-semibold text-gray-900">История событий</div>
                  <button onClick={() => setShareDetailsOpen(false)} className="p-1 rounded hover:bg-gray-100 text-gray-400">
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
                <div className="overflow-y-auto flex-1 px-5 py-4">
                  {shareDetailsLoading ? (
                    <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" /></div>
                  ) : shareDetailsData ? (() => {
                    const { link, views_log, events } = shareDetailsData;
                    const fmt = (iso) => iso ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

                    // Build timeline
                    const timeline = [];
                    if (link.created_at) timeline.push({ at: link.created_at, type: 'created', label: 'Ссылка создана' });
                    (views_log || []).forEach(v => timeline.push({ at: v.at, type: 'view', label: 'Клиент открыл страницу' }));
                    (events || []).forEach(e => {
                      let label = '';
                      if (e.event_type === 'order_click') {
                        const price = e.final_price != null ? `${Math.round(e.final_price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ₸` : '';
                        label = `Нажал «Заказать»${e.product_name ? ': ' + e.product_name : ''}${price ? ' · ' + price : ''}`;
                      } else if (e.event_type === 'whatsapp_click') label = 'Нажал «Написать в WhatsApp»';
                      else if (e.event_type === 'phone_click') label = 'Нажал «Позвонить»';
                      else if (e.event_type === 'map_click') label = 'Открыл в 2GIS';
                      if (label) timeline.push({ at: e.created_at, type: e.event_type, label, extra: e });
                    });
                    timeline.sort((a, b) => new Date(a.at) - new Date(b.at));

                    const dot = (type) => type === 'created' ? 'bg-gray-400' : type === 'view' ? 'bg-blue-400' : type === 'order_click' ? 'bg-green-500' : 'bg-gray-300';

                    return (
                      <>
                        {/* Link info */}
                        <div className="bg-gray-50 rounded-xl p-3 mb-4 text-sm space-y-1">
                          <div><span className="text-gray-500">Клиент:</span> <span className="font-medium">{link.recipient_name || '—'}</span>{link.recipient_phone && <span className="text-gray-500 ml-2">{link.recipient_phone}</span>}</div>
                          <div><span className="text-gray-500">Запрос:</span> <span className="font-medium">{link.params?.q || '—'}</span></div>
                          {link.discount_percent && <div><span className="text-gray-500">Скидка:</span> <span className="font-bold text-red-600">-{link.discount_percent}%</span></div>}
                          <div className="flex gap-4 text-xs text-gray-500 pt-1">
                            <span>Просмотров: <b className="text-gray-800">{link.views || 0}</b></span>
                            <span>Заказов: <b className="text-green-700">{link.order_clicks || 0}</b></span>
                          </div>
                          <div className="text-xs text-gray-400">
                            <a href={`/s/${link.code}`} target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-500">/s/{link.code}</a>
                          </div>
                        </div>

                        {/* Timeline */}
                        {timeline.length === 0 ? (
                          <p className="text-gray-400 text-sm text-center py-6">Событий пока нет</p>
                        ) : (
                          <div className="relative pl-5">
                            <div className="absolute left-1.5 top-0 bottom-0 w-px bg-gray-200" />
                            <div className="space-y-3">
                              {timeline.map((item, i) => (
                                <div key={i} className="relative flex items-start gap-3">
                                  <div className={`absolute -left-3.5 mt-1.5 w-2.5 h-2.5 rounded-full border-2 border-white ${dot(item.type)}`} />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs text-gray-400">{fmt(item.at)}</div>
                                    <div className={`text-sm ${item.type === 'order_click' ? 'font-medium text-green-700' : 'text-gray-700'}`}>{item.label}</div>
                                    {item.extra?.product_name && item.type === 'order_click' && (
                                      <div className="text-xs text-gray-400 mt-0.5">
                                        {[item.extra.brand, item.extra.articul].filter(Boolean).join(' · ')}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })() : null}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Пользователи</CardTitle>
                <CardDescription>Управление ролями: обычный пользователь, менеджер (заказы + каталог), администратор.</CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={fetchUsers} disabled={usersLoading}>
                <RefreshCcw className={`w-4 h-4 mr-2 ${usersLoading ? 'animate-spin' : ''}`} />
                Обновить
              </Button>
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <div className="flex justify-center py-8"><RefreshCcw className="w-6 h-6 animate-spin text-gray-400" /></div>
              ) : (
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        {['Имя', 'Email', 'Роль', 'Дата регистрации', ''].map((h) => (
                          <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {usersList.map((u) => {
                        const currentRole = u.is_admin ? 'admin' : (u.role || 'user');
                        const isUpdating = updatingUserId === u.id;
                        return (
                          <tr key={u.id} className="hover:bg-blue-50/30 transition-colors">
                            <td className="px-4 py-3 font-medium text-gray-900">{u.name}</td>
                            <td className="px-4 py-3 text-gray-500">{u.email}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                currentRole === 'admin'   ? 'bg-red-100 text-red-700' :
                                currentRole === 'manager' ? 'bg-amber-100 text-amber-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {currentRole === 'admin' ? 'Администратор' : currentRole === 'manager' ? 'Менеджер' : 'Пользователь'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-xs">
                              {u.created_at ? new Date(u.created_at).toLocaleDateString('ru-RU') : '—'}
                            </td>
                            <td className="px-4 py-3">
                              <Select
                                value={currentRole}
                                onValueChange={(v) => handleUserRoleChange(u.id, v)}
                                disabled={isUpdating}
                              >
                                <SelectTrigger className="h-7 w-36 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="user">Пользователь</SelectItem>
                                  <SelectItem value="manager">Менеджер</SelectItem>
                                  <SelectItem value="admin">Администратор</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                          </tr>
                        );
                      })}
                      {usersList.length === 0 && (
                        <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Нет пользователей</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default AdminPage;
